import { useState } from 'react'
import {
  CompaniesHeader,
  CompaniesTable,
  type CompanyData,
  type GroupOption,
  type CreateCompanyInput,
} from '@valiant/ui-shared'
import {
  useGetCompaniesQuery,
  useGetGroupsQuery,
  useCreateCompanyMutation,
  useAssignUserToCompaniesMutation,
  useRemoveUserFromCompanyMutation,
  useGetCompanyDepositWalletsQuery,
  CompanyStatus,
  GroupStatus,
} from '@/generated/graphql'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/AuthProvider'
import { useCompaniesFundsFilter, useDebounce } from '@valiant/ui-shared'

interface CompaniesContentProps {
  searchTerm?: string
  groupId?: string
  userId?: string
  preSelectedGroup?: GroupOption
  hideUnassignButton?: boolean
  hideAssignButton?: boolean
}

export function CompaniesContent({
  searchTerm,
  groupId,
  userId,
  preSelectedGroup,
  hideUnassignButton = false,
  hideAssignButton = false,
}: CompaniesContentProps) {
  const navigate = useNavigate()
  const { auth } = useAuth()
  const isComplianceUser = auth?.role === 'COMPLIANCE'
  const [companyId, setCompanyId] = useState('')
  const [modalHandlers, setModalHandlers] = useState<{
    handleCreateClick: () => void
    handleAssignClick: () => void
  } | null>(null)

  const debouncedSearchTerm = useDebounce(searchTerm)
  const { fundsFilter, setFundsFilter } = useCompaniesFundsFilter()

  // GraphQL queries
  const [{ data: companiesData, fetching: companiesLoading }] = useGetCompaniesQuery({
    variables: {
      filter: {
        ...(groupId && { groupId }),
        ...(userId && { userId }),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(fundsFilter === 'withFunds' && { hasFunds: true }),
      },
    },
  })

  // Fetch all companies for assignment dropdown when showing for a specific user
  const [{ data: allCompaniesData }] = useGetCompaniesQuery({
    variables: {
      filter: {
        // Filter by user's group for assignment options
        // ...(userGroupId && { groupId: userGroupId }),
        // No userId filter to get all companies for assignment
        // ...(searchTerm && { search: searchTerm }),
      },
    },
    pause: !userId, // Only fetch when we have a userId (showing user's companies)
  })

  const [{ data: groupsData }] = useGetGroupsQuery({
    variables: {
      filter: { status: GroupStatus.Active },
    },
  })
  const [{ data: walletsData, fetching: walletsLoading }, fetchWallets] =
    useGetCompanyDepositWalletsQuery({
      variables: { companyId },
      pause: !companyId,
    })

  // Mutations
  const [, createCompany] = useCreateCompanyMutation()
  const [, assignUserToCompanies] = useAssignUserToCompaniesMutation()
  const [, removeUserFromCompany] = useRemoveUserFromCompanyMutation()

  // Data mapping
  const companies: CompanyData[] = (companiesData?.getCompanies || []).map((company) => ({
    id: company.id,
    company: company.company,
    group: company.group,
    groupId: company.groupId,
    imageUrl: company.imageUrl || undefined,
    tradeType: company.tradeType as 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED',
    autoApprove: company.autoApprove,
    balance: company.balance,
    parentCompanyId: company.parentCompanyId,
  }))

  // All companies for assignment dropdown
  const allCompanies = (allCompaniesData?.getCompanies || [])
    .filter((company) => !companies.some((c) => c.company === company.company))
    .map((company) => ({
      id: company.id,
      company: company.company,
      group: company.group,
      groupId: company.groupId,
    }))

  const groups: GroupOption[] = (groupsData?.getGroups || []).map((group) => ({
    groupId: group.groupId,
    name: group.name,
    status: group.status as 'ACTIVE' | 'DEACTIVATED',
  }))

  const wallets = walletsData?.getCompanyDepositWallets || []

  // Helper function to map UI tradeType to GraphQL CompanyStatus
  const mapTradeTypeToCompanyStatus = (
    tradeType: 'CRYPTO' | 'CRYPTO_AND_FIAT' | 'DEACTIVATED',
  ): CompanyStatus => {
    switch (tradeType) {
      case 'CRYPTO':
        return CompanyStatus.Crypto
      case 'CRYPTO_AND_FIAT':
        return CompanyStatus.CryptoAndFiat
      case 'DEACTIVATED':
        return CompanyStatus.Deactivated
      default:
        return CompanyStatus.Crypto
    }
  }

  // Callback implementations
  const handleCreateCompany = async (input: CreateCompanyInput) => {
    try {
      const result = await createCompany({
        input: {
          name: input.company,
          groupId: input.groupId,
          status: mapTradeTypeToCompanyStatus(input.tradeType),
          parentCompanyId: input.parentCompanyId,
          autoApprove: input.autoApprove,
        },
      })

      if (result.error) {
        toast.error('Failed to create company', {
          description: result.error.message,
        })
        return
      }

      toast.success('Company created successfully!')
    } catch (error) {
      toast.error('Failed to create company', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const handleAssignUserToCompany = async (targetUserId: string, companyIds: string[]) => {
    try {
      const result = await assignUserToCompanies({
        input: {
          userId: targetUserId,
          companyIds,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      const companyCount = companyIds.length
      toast.success(
        `User successfully assigned to ${companyCount} ${companyCount === 1 ? 'company' : 'companies'}!`,
      )
    } catch (error) {
      toast.error('Failed to assign user to companies', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const handleUnassignUserFromCompany = async (targetUserId: string, companyId: string) => {
    try {
      const result = await removeUserFromCompany({
        input: {
          userId: targetUserId,
          companyId,
        },
      })

      if (result.error) {
        throw new Error(result.error.message)
      }

      toast.success('User successfully unassigned from company!')
    } catch (error) {
      toast.error('Failed to unassign user from company', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
      })
    }
  }

  const handleViewCompanyDetails = (companyId: string) => {
    navigate(`/companies/${companyId}`)
  }

  const handleFetchWallets = (companyId: string) => {
    setCompanyId(companyId)
    fetchWallets({ variables: { companyId } })
  }

  const handleCopyWalletAddress = (address: string) => {
    navigator.clipboard.writeText(address)
    toast.success('Address copied to clipboard!')
  }

  return (
    <div className="space-y-4">
      <CompaniesHeader
        title={userId ? 'User Companies' : 'Companies'}
        showCreateButton={!userId && !isComplianceUser}
        showAssignButton={!!userId && !isComplianceUser}
        onCreateClick={modalHandlers?.handleCreateClick}
        onAssignClick={modalHandlers?.handleAssignClick}
        hideAssignButton={hideAssignButton}
        fundsFilter={fundsFilter}
        onFundsFilterChange={setFundsFilter}
      />

      <CompaniesTable
        companies={companies}
        loading={companiesLoading}
        showCreateButton={!userId && !isComplianceUser}
        showAssignButton={!!userId && !isComplianceUser}
        showWalletsButton={!userId}
        showUnassignButton={!!userId && !isComplianceUser && !hideUnassignButton}
        showAutoApproveToggle={!userId && !isComplianceUser}
        groups={groups}
        allCompanies={allCompanies}
        depositWallets={wallets}
        walletsLoading={walletsLoading}
        onCreateCompany={handleCreateCompany}
        onAssignUserToCompany={
          userId
            ? (targetUserId, companyIds) => handleAssignUserToCompany(targetUserId, companyIds)
            : undefined
        }
        onUnassignUserFromCompany={
          userId
            ? (targetUserId, companyId) => handleUnassignUserFromCompany(targetUserId, companyId)
            : undefined
        }
        onViewCompanyDetails={handleViewCompanyDetails}
        onFetchWallets={handleFetchWallets}
        onCopyWalletAddress={handleCopyWalletAddress}
        userId={userId}
        onModalHandlersReady={setModalHandlers}
        preSelectedGroup={preSelectedGroup}
      />
    </div>
  )
}
